import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, Headphones, Radio, Sparkles } from 'lucide-react';

// Sound wave animation component
const SoundWave = ({ isPlaying }) => {
  return (
    <div className="flex items-center gap-1 h-8">
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className="w-1 bg-gradient-to-t from-teal-500 to-purple-500 rounded-full"
          animate={{
            height: isPlaying
              ? [8, 24, 16, 32, 12, 28, 8][i % 7] + 'px'
              : '8px',
            scaleY: isPlaying ? [1, 1.5, 0.8, 1.2, 1] : 1
          }}
          transition={{
            duration: 0.5,
            repeat: isPlaying ? Infinity : 0,
            repeatType: 'reverse',
            delay: i * 0.1
          }}
        />
      ))}
    </div>
  );
};

// Floating music notes
const FloatingNotes = () => {
  const notes = ['♪', '♫', '♬', '♩'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(12)].map((_, i) => (
        <motion.span
          key={i}
          className="absolute text-2xl text-purple-400/20"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            rotate: [-15, 15, -15],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{
            duration: 4 + Math.random() * 3,
            delay: Math.random() * 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          {notes[i % notes.length]}
        </motion.span>
      ))}
    </div>
  );
};

const PodcastSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);

  const episode = {
    title: 'The Mystery of the Vanishing Cookies',
    duration: '04:37',
    description: "Join Ned and Nancy in their first audio adventure as they uncover the secrets behind mysterious vanishing cookies!",
    audioSrc: '/audio/podcast.mp3'
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        setProgress((audio.currentTime / audio.duration) * 100);
      };
      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, []);

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e) => {
    const newProgress = parseFloat(e.target.value);
    setProgress(newProgress);
    if (audioRef.current) {
      audioRef.current.currentTime = (newProgress / 100) * audioRef.current.duration;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        Math.max(0, audioRef.current.currentTime + seconds),
        audioRef.current.duration
      );
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #0f0f2a 0%, #1a1a3a 50%, #0a0a1a 100%)'
          }}
        />

        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 60%)',
            filter: 'blur(80px)'
          }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, -50, 0]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 left-0 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(20, 184, 166, 0.15) 0%, transparent 60%)',
            filter: 'blur(80px)'
          }}
          animate={{
            scale: [1.2, 1, 1.2],
            x: [0, 50, 0]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <FloatingNotes />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-teal-500" />
            <Headphones className="w-8 h-8 text-teal-400" />
            <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-teal-500" />
          </div>

          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="gradient-text">Ned & Nancy Podcast</span>
          </h2>

          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-serif italic">
            Listen to their adventures come alive! Immersive audio stories for curious minds.
          </p>
        </motion.div>

        {/* Main player card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto mb-16"
        >
          {/* Gradient border wrapper */}
          <div className="relative p-[2px] rounded-3xl bg-gradient-to-br from-purple-500 via-teal-500 to-amber-500">
            <div className="bg-[#0f0f2a] rounded-3xl p-8 lg:p-10">
              {/* Episode info */}
              <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                {/* Album art */}
                <motion.div
                  className="relative w-32 h-32 lg:w-40 lg:h-40 flex-shrink-0"
                  whileHover={{ scale: 1.05, rotate: 3 }}
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500 to-teal-500 animate-pulse-glow" />
                  <div className="relative w-full h-full rounded-2xl overflow-hidden">
                    <img
                      src="/images/episode.webp"
                      alt="Episode Cover"
                      className="w-full h-full object-cover"
                    />
                    {/* Vinyl record overlay effect */}
                    <motion.div
                      className="absolute inset-0 rounded-full bg-black/20"
                      animate={isPlaying ? { rotate: 360 } : {}}
                      transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                      style={{
                        background: 'conic-gradient(from 0deg, transparent, rgba(0,0,0,0.3), transparent)'
                      }}
                    />
                  </div>

                  {/* Live indicator */}
                  <motion.div
                    className="absolute -top-2 -right-2 flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-teal-500"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Radio className="w-3 h-3" />
                    <span className="text-xs font-bold">NEW</span>
                  </motion.div>
                </motion.div>

                {/* Episode details */}
                <div className="text-center md:text-left flex-1">
                  <span className="text-sm text-purple-400 uppercase tracking-widest mb-2 block">
                    Now Playing
                  </span>
                  <h3 className="font-display text-2xl lg:text-3xl font-bold text-white mb-3">
                    {episode.title}
                  </h3>
                  <p className="text-gray-400 text-sm lg:text-base leading-relaxed">
                    {episode.description}
                  </p>

                  {/* Sound wave visualization */}
                  <div className="mt-4 flex items-center justify-center md:justify-start gap-4">
                    <SoundWave isPlaying={isPlaying} />
                    <span className="text-sm text-gray-500">
                      {isPlaying ? 'Playing...' : 'Ready to play'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Audio element */}
              <audio ref={audioRef} src={episode.audioSrc} />

              {/* Progress bar */}
              <div className="mb-6">
                <div className="relative h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                  <motion.div
                    className="absolute left-0 top-0 h-full rounded-full"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #6b4c9a, #14b8a6)'
                    }}
                    layoutId="progress"
                  />
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={handleSeek}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{formatTime(currentTime)}</span>
                  <span>{episode.duration}</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between">
                {/* Skip backward */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => skip(-10)}
                  className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <SkipBack className="w-5 h-5 text-gray-400" />
                </motion.button>

                {/* Play/Pause */}
                <motion.button
                  whileHover={{ scale: 1.1, boxShadow: '0 0 40px rgba(107, 76, 154, 0.5)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={togglePlayPause}
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #6b4c9a, #14b8a6)'
                  }}
                >
                  {isPlaying ? (
                    <Pause className="w-7 h-7 text-white" />
                  ) : (
                    <Play className="w-7 h-7 text-white ml-1" />
                  )}
                </motion.button>

                {/* Skip forward */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => skip(10)}
                  className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <SkipForward className="w-5 h-5 text-gray-400" />
                </motion.button>

                {/* Volume */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleMute}
                  className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  {isMuted ? (
                    <VolumeX className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-gray-400" />
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Subscription card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto text-center"
        >
          <div className="relative p-[1px] rounded-2xl bg-gradient-to-r from-amber-400 via-purple-500 to-teal-400">
            <div className="bg-[#0f0f2a] rounded-2xl p-8">
              <motion.div
                className="inline-block mb-4"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Sparkles className="w-10 h-10 text-amber-400" />
              </motion.div>

              <h3 className="font-display text-2xl font-bold text-white mb-3">
                Subscribe for More Adventures!
              </h3>
              <p className="text-gray-400 mb-6 text-sm">
                Get exclusive access to weekly episodes, behind-the-scenes content, and interactive mysteries!
              </p>

              <div className="text-3xl font-bold gradient-text-gold mb-6">
                $4.99<span className="text-lg text-gray-400">/month</span>
              </div>

              <motion.a
                href="https://podcasts.apple.com/us/podcast/bite-sized-adventures-with-ned-and-nancy/id1724601433"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(245, 158, 11, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)'
                }}
              >
                <img src="/images/apple.png" alt="Apple Podcasts" className="w-6 h-6" />
                <span className="text-[#0a0a1a]">Subscribe on Apple Podcasts</span>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PodcastSection;
